import { ChatMessage, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { Paperclip } from "react-feather";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import type {
  SupportTicketAttachment,
  SupportTicketComment,
} from "../supportTicketTypes";

/**
 * Props for {@link SupportTicketCommentItem}.
 */
interface SupportTicketCommentItemProps {
  comment: SupportTicketComment;
}

/**
 * Opens an attachment in a new tab, resolving its download URL on demand.
 *
 * @param gcsPath - Firebase Storage path of the attachment.
 */
const openAttachment = async (gcsPath: string) => {
  const url = await getFileDownloadUrl(gcsPath);
  if (!url) {
    toast.error("Unable to open the attachment.");
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * A clickable paperclip that opens one attachment.
 *
 * @param props - The attachment to open.
 * @returns Clip icon button.
 */
const AttachmentClip = ({
  attachment,
}: {
  attachment: SupportTicketAttachment;
}) => (
  <button
    type="button"
    onClick={() => {
      void openAttachment(attachment.gcsPath);
    }}
    className="text-tertiaryBlue-5 transition-colors hover:text-tertiaryBlue-4"
    title={attachment.filename}
    aria-label={`Open attachment ${attachment.filename}`}
  >
    <Paperclip className="h-4 w-4" aria-hidden="true" />
  </button>
);

/**
 * Renders a single support ticket thread item using the Oasis chat message
 * pattern. The bubble holds only the text; attachment clip icons render in the
 * accessory slot, outside the bubble.
 *
 * @param props - Comment (with optional attachments) for one thread row.
 * @returns Styled chat message row.
 */
const SupportTicketCommentItem = ({
  comment,
}: SupportTicketCommentItemProps) => {
  const attachments = comment.attachments ?? [];

  const accessory =
    attachments.length > 0 ? (
      <span className="inline-flex flex-wrap items-center gap-2">
        {attachments.map((attachment) => (
          <AttachmentClip key={attachment.id} attachment={attachment} />
        ))}
      </span>
    ) : undefined;

  return (
    <ChatMessage
      senderName={comment.senderName}
      timestamp={comment.timestamp}
      message={comment.message}
      side={comment.side}
      avatarLabel={comment.avatarLabel}
      maxWidth="75%"
      bubbleClassName="rounded border-0 bg-[#F7F9FA] text-tiny text-primaryGray-4"
      accessory={accessory}
      accessoryAriaLabel={attachments.length > 0 ? "Attachments" : undefined}
    />
  );
};

export default SupportTicketCommentItem;
